import * as Sentry from '@sentry/react';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import isoWeek from 'dayjs/plugin/isoWeek';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import '@carbon/charts/styles.css';
import '@carbon/styles/css/styles.css';
import App from 'src/App';
import { CrudProviders } from 'src/crud/CrudProviders';
import 'src/index.scss';
import { createFilteredConsoleError } from 'src/utils/consoleFilter';
import { locale } from 'src/utils/settings';
import './i18n';

dayjs.extend(relativeTime);
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale(locale);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Load-bearing for correctness: `useReferenceOptions` and `useCrudResource` opt back into
      // refetchOnMount because of this default, without which a stale-marking invalidation from
      // a CRUD mutation would never reach a reference list or a list the user left and returned
      // to. Read those two before flipping it.
      refetchOnMount: false,
    },
  },
});

if (import.meta.env.VITE_SENTRY_DNS_URL && import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DNS_URL,
    integrations: [
      new Sentry.BrowserTracing(),
      new Sentry.Replay({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
  });
}

console.error = createFilteredConsoleError(console.error.bind(console));

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <CrudProviders>
          <App />
        </CrudProviders>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
